import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from 'xlsx';
import {
  GiftIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowRightIcon,
  TrophyIcon,
  ChartPieIcon,
  ShoppingBagIcon,
  PaperAirplaneIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import api from "../services/api"; // IMPORTANT: Using your central API instance
import { Doughnut, Pie, Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, LineElement, PointElement);

export default function BonusPage() {
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterYear, setFilterYear] = useState("All");
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [rewardModal, setRewardModal] = useState(null);
  const [chartType, setChartType] = useState(0); // 0 is Pie Chart
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  // ===============================================
  // REAL API INTEGRATION
  // ===============================================
  useEffect(() => {
    const fetchEmployeeRewards = async () => {
      try {
        setLoading(true);
        const response = await api.get("/rewards/employee/employee/rewards/");
        const data = response.data || response;
        setBonuses(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        console.error("API Error:", err);
        setError(err.response?.data?.detail || "Unable to load rewards.");
      } finally {
        setLoading(false);
      }
    };
    fetchEmployeeRewards();
  }, []);

  useEffect(() => {
    setChartType(0); // Ensure Pie chart is default when filter changes
  }, [filterYear]);

  // ===============================================
  // FILTERING LOGIC
  // ===============================================
  const years = ["All", ...new Set(bonuses.map(b => b.year || moment(b.date).year()))].sort((a, b) => b - a);

  const filteredBonuses = bonuses.filter((b) => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      (b.type?.toLowerCase().includes(searchLower)) ||
      (String(b.amount).includes(search)) ||
      (b.description?.toLowerCase().includes(searchLower));
    
    const bYear = b.year || moment(b.date).year();
    const matchesYear = filterYear === "All" || bYear === Number(filterYear);
    
    // Apply date range filter
    let matchesDateRange = true;
    if (dateRange.from && dateRange.to) {
      const bonusDate = moment(b.date);
      const fromDate = moment(dateRange.from);
      const toDate = moment(dateRange.to);
      matchesDateRange = bonusDate.isBetween(fromDate, toDate, 'day', '[]');
    }
    
    return matchesSearch && matchesYear && matchesDateRange;
  });

  const totalEarned = bonuses.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
  const thisYearEarned = bonuses
    .filter(b => (b.year || moment(b.date).year()) === moment().year())
    .reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
  const totalRecords = bonuses.length;

  // ===============================================
  // CHART DATA & VIBRANT COLORS
  // ===============================================
  const getFilteredForCharts = () => filterYear === "All" ? bonuses : bonuses.filter(b => (b.year || moment(b.date).year()) === Number(filterYear));

  // Vibrant Palette
  const vibrantColors = ['#6366F1', '#10B981', '#F59E0B', '#F43F5E', '#8B5CF6', '#3B82F6', '#EC4899', '#06B6D4'];

  const pieData = {
    labels: getFilteredForCharts().map(b => b.type || "Reward"),
    datasets: [{
      data: getFilteredForCharts().map(b => Number(b.amount)),
      backgroundColor: vibrantColors,
      hoverOffset: 15,
      borderWidth: 2,
      borderColor: '#ffffff',
    }],
  };

  const doughnutData = {
    labels: years.filter(y => y !== "All"),
    datasets: [{
      data: years.filter(y => y !== "All").map(y =>
        bonuses.filter(b => (b.year || moment(b.date).year()) === Number(y)).reduce((sum, b) => sum + Number(b.amount), 0)
      ),
      backgroundColor: ['#6366F1', '#10B981', '#F59E0B', '#F43F5E'],
      borderWidth: 0,
      cutout: '68%',
    }],
  };

  const barData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Earnings',
        data: Array(12).fill(0).map((_, i) => 
            bonuses.filter(b => moment(b.date).month() === i).reduce((sum, b) => sum + Number(b.amount), 0)
        ),
        backgroundColor: '#6366F1',
        borderRadius: 8,
      }
    ],
  };

  const lineData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
    datasets: [
      {
        label: 'Trend',
        data: [0, totalEarned * 0.2, totalEarned * 0.4, totalEarned * 0.6, totalEarned * 0.8, totalEarned * 0.9, totalEarned],
        borderColor: '#10B981',
        backgroundColor: 'rgba(16,185,129,0.1)',
        tension: 0.4,
        fill: true,
      }
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { font: { family: 'Poppins', size: 12 } } },
      tooltip: {
        callbacks: {
          label: (context) => ` ₹${context.raw.toLocaleString()}`
        }
      }
    }
  };

  const chartComponents = [
    <Pie key="pie" data={pieData} options={chartOptions} />,
    <Doughnut key="doughnut" data={doughnutData} options={chartOptions} />,
    <Bar key="bar" data={barData} options={chartOptions} />,
    <Line key="line" data={lineData} options={chartOptions} />,
  ];

  const downloadRewardHistory = () => {
    try {
      const emp_code = localStorage.getItem('emp_code') || 'EMPLOYEE';
      const emp_name = localStorage.getItem('user_name') || 'Employee';

      // Metadata rows
      const metadataRows = [
        { 'Reward History Report': 'Employee Code', 'Value': emp_code },
        { 'Reward History Report': 'Employee Name', 'Value': emp_name },
        { 'Reward History Report': 'Report Type', 'Value': 'Bonus & Incentives History' },
        { 'Reward History Report': 'Year Filter', 'Value': filterYear },
        { 'Reward History Report': 'Date Range', 'Value': dateRange.from && dateRange.to ? `${dateRange.from} to ${dateRange.to}` : 'All Time' },
        { 'Reward History Report': 'Total Records', 'Value': filteredBonuses.length },
        { 'Reward History Report': 'Total Amount', 'Value': `₹${filteredBonuses.reduce((sum, b) => sum + Number(b.amount), 0).toLocaleString()}` },
        { 'Reward History Report': 'Generated On', 'Value': new Date().toLocaleString() },
        { 'Reward History Report': '', 'Value': '' }
      ];

      // Data rows
      const dataRows = filteredBonuses.map(bonus => ({
        'Year': bonus.year || moment(bonus.date).year(),
        'Type': bonus.type || 'Performance',
        'Amount (₹)': Number(bonus.amount),
        'Status': bonus.status || 'Pending',
        'Date': moment(bonus.date).format('DD MMM YYYY'),
        'Description': bonus.description || 'N/A'
      }));

      const allRows = [...metadataRows, ...dataRows];
      const ws = XLSX.utils.json_to_sheet(allRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Reward History');
      XLSX.writeFile(wb, `Reward_History_${new Date().getTime()}.xlsx`);
    } catch (err) {
      console.error('Download error:', err);
      alert('Download failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
          <p className="text-sm font-medium text-gray-500 font-poppins">Gathering your rewards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-4">
        <div className="text-center font-poppins">
          <ExclamationCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-gray-900">{error}</h2>
          <button onClick={() => window.location.reload()} className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 antialiased relative font-roboto">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Roboto:wght@400;500;700&display=swap');
        .font-poppins { font-family: 'Poppins', sans-serif; }
        .font-roboto { font-family: 'Roboto', sans-serif; }
      `}</style>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Header Section */}
        <div className="mb-10 font-poppins">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 p-3 rounded-xl shadow-md">
              <GiftIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Bonus & Incentives</h1>
              <p className="text-gray-600 mt-1">Verified rewards and performance payouts</p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          <SummaryCardItem label="Total Earned" value={`₹${totalEarned.toLocaleString()}`} subText="Lifetime Earnings" color="indigo" />
          <SummaryCardItem label="This Year" value={`₹${thisYearEarned.toLocaleString()}`} subText={`${moment().year()} Total`} color="amber" />
          <SummaryCardItem label="Total Records" value={totalRecords} subText="Entries Found" color="emerald" />
        </div>

        {/* Chart Section */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xl shadow-gray-100/50 mb-12">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800 font-poppins flex items-center gap-2">
              <ChartPieIcon className="h-5 w-5 text-indigo-600" />
              Earnings Visualization
            </h3>
            <button
              onClick={() => setChartType((prev) => (prev + 1) % 4)}
              className="text-xs font-bold bg-indigo-50 text-indigo-600 px-4 py-2 rounded-xl hover:bg-indigo-100 transition-colors flex items-center gap-2"
            >
              Toggle View <ArrowRightIcon className="h-3 w-3" />
            </button>
          </div>
          <div className="h-80">
            {chartComponents[chartType]}
          </div>
        </div>

        {/* Search & Filter Table */}
        <div className="bg-white border border-gray-100 rounded-3xl shadow-xl shadow-gray-100/50 overflow-hidden">
          {/* Reward History Header with Buttons */}
          <div className="p-6 border-b border-gray-100 bg-gray-50/50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800 font-poppins">Reward History</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowHistoryModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition"
                >
                  <ClockIcon className="w-3.5 h-3.5" />
                  View History
                </button>
                <button
                  onClick={downloadRewardHistory}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold transition"
                >
                  <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                  Download
                </button>
            </div>
          </div>

          {/* Search and Filter Row */}
          <div className="p-6 border-b border-gray-100 bg-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="relative w-full sm:w-96">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search rewards..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 text-sm border border-gray-200 rounded-2xl bg-white focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all"
                />
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-gray-500 font-poppins">Year:</span>
                <select
                  className="text-sm border border-gray-200 rounded-2xl px-5 py-2.5 bg-white outline-none focus:ring-4 focus:ring-indigo-500/10 font-medium"
                  value={filterYear}
                  onChange={(e) => setFilterYear(e.target.value)}
                >
                  {years.map((year) => <option key={year} value={year}>{year}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/50">
                <tr>
                  <TH text="Year" />
                  <TH text="Type" />
                  <TH text="Amount" />
                  <TH text="Status" />
                  <TH text="Date" />
                  <TH text="Action" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredBonuses.length > 0 ? (
                  filteredBonuses.map((bonus, idx) => (
                    <tr key={bonus.id || idx} className="hover:bg-indigo-50/30 transition-colors group">
                      <td className="px-6 py-5 text-sm font-medium text-gray-500">{bonus.year || moment(bonus.date).year()}</td>
                      <td className="px-6 py-5 text-sm font-bold text-gray-900 font-poppins">{bonus.type || "Performance"}</td>
                      <td className="px-6 py-5 text-sm font-bold text-indigo-600 font-roboto">₹{Number(bonus.amount)?.toLocaleString()}</td>
                      <td className="px-6 py-5">
                        <span className={`inline-flex px-3 py-1 text-[10px] font-bold uppercase rounded-lg ${
                          bonus.status === "Paid" || bonus.status === "Approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {bonus.status || "Pending"}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-sm text-gray-500">
                        {moment(bonus.date).format("DD MMM YYYY")}
                      </td>
                      <td className="px-6 py-5 text-right">
                        <button
                          onClick={() => setSelectedBonus(bonus)}
                          className="text-indigo-600 group-hover:bg-indigo-100 p-2 rounded-lg transition-all"
                        >
                          <ArrowRightIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-20 text-center text-gray-400 font-poppins">No reward data found for this period.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Redemption Portal */}
        <div className="mt-16">
          <h3 className="text-xl font-bold text-gray-900 font-poppins mb-8 flex items-center gap-3">
            <TrophyIcon className="h-6 w-6 text-amber-500" />
            Redeem Rewards
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {[
              { icon: GiftIcon, label: "Gift Cards", color: "indigo" },
              { icon: PaperAirplaneIcon, label: "Travel Points", color: "sky" },
              { icon: ShoppingBagIcon, label: "Shopping", color: "pink" },
              { icon: TrophyIcon, label: "Cash Out", color: "amber" },
            ].map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -10, shadow: "0 20px 25px -5px rgb(0 0 0 / 0.1)" }}
                onClick={() => setRewardModal(item.label)}
                className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm cursor-pointer flex flex-col items-center text-center"
              >
                <div className={`p-4 rounded-2xl mb-4 bg-${item.color}-50`}>
                  <item.icon className={`h-8 w-8 text-${item.color}-600`} />
                </div>
                <p className="text-xs font-bold text-gray-800 font-poppins">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {rewardModal && (
          <Modal onClose={() => setRewardModal(null)}>
            <div className="font-poppins">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Redeem {rewardModal}</h2>
              <p className="text-sm text-gray-500 mb-8 leading-relaxed">Request conversion of your bonus balance into {rewardModal}. This will be processed in your next cycle.</p>
              <div className="bg-indigo-50 p-4 rounded-2xl border border-indigo-100 mb-8">
                <p className="text-xs text-indigo-700 italic font-medium tracking-tight text-center">"Your request will be sent to HR for final validation."</p>
              </div>
              <button onClick={() => setRewardModal(null)} className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg shadow-indigo-200">Submit Request</button>
            </div>
          </Modal>
        )}

        {selectedBonus && (
          <Modal onClose={() => setSelectedBonus(null)}>
            <div className="font-roboto">
              <h2 className="text-2xl font-bold text-gray-900 mb-1 font-poppins">Reward Details</h2>
              <p className="text-xs font-bold text-indigo-600 mb-8 tracking-widest uppercase">TXN ID: {selectedBonus.id || 'N/A'}</p>
              <div className="space-y-6">
                <DetailRow label="Type" value={selectedBonus.type || "Incentive"} />
                <div className="grid grid-cols-2 gap-8 py-6 border-y border-gray-100">
                  <DetailRow label="Amount" value={`₹${Number(selectedBonus.amount)?.toLocaleString()}`} isBold />
                  <DetailRow label="Fiscal Year" value={selectedBonus.year || moment(selectedBonus.date).year()} />
                </div>
                <DetailRow label="Manager Remarks" value={selectedBonus.description || "Consistent high performance and contribution."} isItalic />
              </div>
              <button onClick={() => setSelectedBonus(null)} className="mt-10 w-full bg-gray-900 text-white py-4 rounded-2xl font-bold">Close View</button>
            </div>
          </Modal>
        )}

        {showHistoryModal && (
          <Modal onClose={() => {
            setShowHistoryModal(false);
            setDateRange({ from: '', to: '' });
          }}>
            <div className="font-poppins">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">View Reward History</h2>
              <p className="text-sm text-gray-500 mb-6">Select date range to filter reward records</p>
              
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-2">From Date</label>
                  <input
                    type="date"
                    value={dateRange.from}
                    onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-2">To Date</label>
                  <input
                    type="date"
                    value={dateRange.to}
                    onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setDateRange({ from: '', to: '' })}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50 transition"
                  >
                    Clear Filter
                  </button>
                  <button
                    onClick={() => setShowHistoryModal(false)}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition"
                  >
                    Apply Filter
                  </button>
                </div>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}

// ===============================================
// MODULAR SUB-COMPONENTS
// ===============================================
const SummaryCardItem = ({ label, value, subText, color }) => {
    const colorClasses = {
        indigo: "text-indigo-600 border-indigo-100 bg-indigo-50/30",
        amber: "text-amber-600 border-amber-100 bg-amber-50/30",
        emerald: "text-emerald-600 border-emerald-100 bg-emerald-50/30"
    };
    return (
        <motion.div whileHover={{ y: -5 }} className={`bg-white border ${colorClasses[color].split(' ')[1]} rounded-[2rem] p-6 shadow-sm`}>
            <p className={`text-[10px] uppercase font-bold tracking-widest ${colorClasses[color].split(' ')[0]} font-poppins`}>{label}</p>
            <p className="text-3xl font-bold text-gray-900 mt-2 font-roboto">{value}</p>
            <p className="text-[10px] text-gray-500 mt-1 font-roboto font-medium">{subText}</p>
        </motion.div>
    );
};

const TH = ({ text }) => (
  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-poppins">{text}</th>
);

const DetailRow = ({ label, value, isBold, isItalic }) => (
  <div>
    <p className="text-[10px] uppercase font-bold text-gray-400 mb-1 tracking-wider">{label}</p>
    <p className={`${isBold ? 'text-2xl font-bold text-indigo-600' : 'text-base font-semibold text-gray-800'} ${isItalic ? 'italic text-gray-500 font-normal leading-relaxed' : ''}`}>{value}</p>
  </div>
);

const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-slate-900/60 backdrop-blur-md" />
    <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="relative bg-white rounded-[2.5rem] p-10 w-full max-w-lg border border-white/20 z-10 shadow-2xl">
      <button onClick={onClose} className="absolute right-8 top-8 p-2 bg-gray-100 rounded-full text-gray-400 hover:text-gray-700 transition-colors"><XMarkIcon className="h-5 w-5" /></button>
      {children}
    </motion.div>
  </div>
);